/**
 * Template resolution for binding pipelines.
 *
 * Templates are `{{path.to.value}}` with optional pipe filters:
 *   {{city}}                 → scope.city
 *   {{item.slug}}            → scope.item.slug
 *   {{date|iso}}             → 2026-08-29
 *   {{date|compact}}         → 20260829
 *   {{title|lower|trim}}     → chained
 *
 * Deliberately not an expression language. Manifests are LLM-generated and
 * served multi-tenant; anything evaluable is an injection surface. Paths and a
 * closed set of filters is the entire grammar.
 */

export type Scope = Record<string, unknown>;

const FILTERS: Record<string, (v: unknown) => unknown> = {
  iso: (v) => toDate(v)?.toISOString().slice(0, 10) ?? '',
  /** CineStar's page URLs use YYYYMMDD while its JSON API demands YYYY-MM-DD. */
  compact: (v) => toDate(v)?.toISOString().slice(0, 10).replace(/-/g, '') ?? '',
  lower: (v) => String(v ?? '').toLowerCase(),
  upper: (v) => String(v ?? '').toUpperCase(),
  trim: (v) => String(v ?? '').trim(),
  slug: (v) =>
    String(v ?? '')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[đĐ]/g, 'd')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, ''),
  number: (v) => {
    const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : null;
  },
  first: (v) => (Array.isArray(v) ? v[0] : v),
  join: (v) => (Array.isArray(v) ? v.join(',') : String(v ?? '')),
  /** Minutes since midnight, so HH:MM can be compared numerically. */
  minutes: (v) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(v ?? ''));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  },
};

function toDate(v: unknown): Date | null {
  if (v instanceof Date) return v;
  const s = String(v ?? '').trim();
  if (!s) return null;
  const d = new Date(/^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}` : s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function getPath(scope: Scope, path: string): unknown {
  let cur: unknown = scope;
  for (const seg of path.split('.')) {
    if (cur == null) return undefined;
    if (Array.isArray(cur) && /^\d+$/.test(seg)) cur = cur[Number(seg)];
    else if (typeof cur === 'object') cur = (cur as Record<string, unknown>)[seg];
    else return undefined;
  }
  return cur;
}

function applyExpr(scope: Scope, expr: string): unknown {
  const [path, ...filters] = expr.split('|').map((s) => s.trim());
  let value = getPath(scope, path!);
  for (const f of filters) {
    const fn = FILTERS[f];
    if (!fn) throw new Error(`unknown template filter "${f}"`);
    value = fn(value);
  }
  return value;
}

/**
 * Resolve a template. A string that is exactly one expression returns the raw
 * typed value (so `{{cinemas}}` yields an array, not "[object Object]");
 * anything else is string interpolation.
 */
export function interpolate(template: string, scope: Scope): unknown {
  const whole = /^\{\{([^}]+)\}\}$/.exec(template.trim());
  if (whole) return applyExpr(scope, whole[1]!);

  return template.replace(/\{\{([^}]+)\}\}/g, (_, expr: string) => {
    const v = applyExpr(scope, expr);
    return v == null ? '' : String(v);
  });
}

/** Resolve a record of templates, dropping keys whose value resolves to empty. */
export function interpolateRecord(
  rec: Record<string, string>,
  scope: Scope,
  { dropEmpty = true } = {},
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, tpl] of Object.entries(rec)) {
    const v = interpolate(tpl, scope);
    if (dropEmpty && (v == null || v === '')) continue;
    out[k] = String(v);
  }
  return out;
}
